import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, Inject, Injectable, OnDestroy, Optional, signal, Signal, WritableSignal } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import type { CompanyIdentity } from '@/shared/models/company-identity.model';
import { JMIX_UI_URL, PYTHON_URL } from '@/shared/config/base-url.token';
import { QuotaOperationIdService } from '@/shared/services/quota-operation-id.service';

interface TariffInfo {
  code: string | null;
  name: string | null;
  remainingAiCalls: number | null;
  validUntil: string | null;
}

interface DataResponse {
  user: string;
  userId: string | null;
  name: string | null;
  role: string | null;
  tariff: TariffInfo | null;
  subscriptionEnd: string | null;
  companyName: string | null;
  companyId: string | null;
  inn: string | null;
  companyIdentity?: CompanyIdentity | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  readonly isAuthenticated$: WritableSignal<boolean>;
  readonly username$: WritableSignal<string | null>;
  readonly userId$: WritableSignal<string | null>;
  readonly name$: WritableSignal<string | null>;
  readonly displayName$: Signal<string | null>;
  readonly role$: WritableSignal<string | null>;
  readonly tariff$: WritableSignal<TariffInfo | null>;
  readonly subscriptionEnd$: WritableSignal<string | null>;
  readonly companyName$: WritableSignal<string | null>;
  readonly companyId$: WritableSignal<string | null>;
  readonly inn$: WritableSignal<string | null>;
  readonly companyIdentity$: WritableSignal<CompanyIdentity | null>;
  private readonly tariffRefreshMs: number = 10_000;
  private tariffRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private logoutInProgress: boolean = false;

  private readonly pythonUrl: string;
  private readonly jmixUiUrl: string;

  constructor(
    private readonly http: HttpClient,
    @Optional() @Inject(PYTHON_URL) pythonUrl: string | null,
    @Optional() @Inject(JMIX_UI_URL) jmixUiUrl: string | null,
    private readonly quotaOperationIds: QuotaOperationIdService,
  ) {
    this.pythonUrl = pythonUrl ?? '/api';
    this.jmixUiUrl = jmixUiUrl ?? '';
    this.isAuthenticated$ = signal(this.readStoredAuth());
    this.username$ = signal(null);
    this.userId$ = signal(null);
    this.name$ = signal(null);
    this.displayName$ = computed(() => this.name$() ?? this.username$());
    this.role$ = signal(null);
    this.tariff$ = signal(null);
    this.subscriptionEnd$ = signal(null);
    this.companyName$ = signal(null);
    this.companyId$ = signal(null);
    this.inn$ = signal(null);
    this.companyIdentity$ = signal(null);
  }

  checkSession(): Observable<void> {
    return this.http.get<DataResponse>(`${this.pythonUrl}/auth/api/data`, { withCredentials: true }).pipe(
      map(data => {
        if (this.logoutInProgress) {
          return;
        }
        this.setAuthenticated(true);
        this.username$.set(data.user);
        this.userId$.set(data.userId ?? null);
        this.name$.set(data.name ?? null);
        this.role$.set(data.role ?? null);
        this.tariff$.set(data.tariff ?? null);
        this.subscriptionEnd$.set(data.subscriptionEnd ?? null);
        this.companyName$.set(data.companyName ?? null);
        this.companyId$.set(data.companyId ?? null);
        this.inn$.set(data.inn ?? null);
        this.companyIdentity$.set(data.companyIdentity ?? null);
      }),
      catchError((error: HttpErrorResponse) => {
        this.resetSessionState(error.status === 401 || error.status === 403);
        return of(undefined);
      }),
    );
  }

  refreshTariff(): Observable<TariffInfo | null> {
    return this.http.get<TariffInfo | null>(`${this.pythonUrl}/auth/api/data/tariff`, { withCredentials: true }).pipe(
      map(tariff => {
        this.tariff$.set(tariff ?? null);
        return tariff ?? null;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.clearSession();
        }
        return of(this.tariff$());
      }),
    );
  }

  redirectToSso(): void {
    const lastRedirect = ((): string | null => {
      try {
        return sessionStorage.getItem('sso_redirect_ts');
      } catch {
        return null;
      }
    })();
    if (lastRedirect && Date.now() - Number(lastRedirect) < 10_000) {
      return;
    }
    try {
      sessionStorage.setItem('sso_redirect_ts', String(Date.now()));
    } catch {
      /* ignore */
    }
    window.location.assign(`${this.jmixUiUrl}/angular-app?returnTo=${encodeURIComponent(window.location.href)}`);
  }

  logout(): void {
    if (this.logoutInProgress) {
      return;
    }
    this.logoutInProgress = true;
    this.clearSession();
    this.http
      .post<DataResponse>(`${this.pythonUrl}/auth/api/logout`, null, { withCredentials: true })
      .pipe(catchError(() => of(undefined)))
      .subscribe(() => {
        window.location.href = `${this.jmixUiUrl}/logout`;
      });
  }

  isLogoutInProgress(): boolean {
    return this.logoutInProgress;
  }

  clearSession(): void {
    this.resetSessionState(true);
  }

  private resetSessionState(persistSessionStatus: boolean): void {
    if (persistSessionStatus) {
      this.quotaOperationIds.clearAll();
    }
    // Любая ошибка закрывает UI; маркер в sessionStorage удаляется только при явном отказе сервера.
    this.setAuthenticated(false, persistSessionStatus);
    this.username$.set(null);
    this.userId$.set(null);
    this.name$.set(null);
    this.role$.set(null);
    this.tariff$.set(null);
    this.subscriptionEnd$.set(null);
    this.companyName$.set(null);
    this.companyId$.set(null);
    this.inn$.set(null);
    this.companyIdentity$.set(null);
  }

  private setAuthenticated(value: boolean, persistSessionStatus: boolean = true): void {
    this.isAuthenticated$.set(value);
    if (value) {
      this.startTariffRefresh();
    } else {
      this.stopTariffRefresh();
    }
    if (!persistSessionStatus) {
      return;
    }
    try {
      sessionStorage.setItem('is_authenticated', String(value));
    } catch {
      // SSR or private browsing — ignore
    }
  }

  ngOnDestroy(): void {
    this.stopTariffRefresh();
  }

  private startTariffRefresh(): void {
    if (this.tariffRefreshTimer !== null) {
      return;
    }
    this.tariffRefreshTimer = setInterval(() => {
      this.refreshTariff().subscribe();
    }, this.tariffRefreshMs);
  }

  private stopTariffRefresh(): void {
    if (this.tariffRefreshTimer !== null) {
      clearInterval(this.tariffRefreshTimer);
      this.tariffRefreshTimer = null;
    }
  }

  private readStoredAuth(): boolean {
    try {
      return sessionStorage.getItem('is_authenticated') === 'true';
    } catch {
      return false;
    }
  }
}
